'use client';

import clsx from 'clsx';
import { InputHTMLAttributes, ReactNode, RefCallback, RefObject } from 'react';
import { FieldError } from 'react-hook-form';

interface TextInputProps extends InputHTMLAttributes<HTMLInputElement> {
  ref?: RefObject<HTMLInputElement | null> | RefCallback<HTMLInputElement | null>;
  label?: string;
  error?: FieldError;
  hint?: string;
  inputLabelClass?: string;
  leftContent?: ReactNode;
  rightContent?: ReactNode;
}

/**
 * @param disabled - disables input and removes its data from the form submit.
 * @param readOnly - only disables input, its data is submitted to the form.
 */
export function TextInput({
  ref,
  className,
  error,
  label,
  hint,
  inputLabelClass,
  leftContent,
  rightContent,
  ...restProps
}: TextInputProps) {
  return (
    <fieldset className={clsx('fieldset', className)}>
      {label && (
        <legend className="fieldset-legend flex-wrap items-baseline justify-start gap-x-3 gap-y-0">
          {label}
          {restProps.required && '*'}
          {error && (
            <span className="validator-hint text-error mt-0">{error?.message?.toString()}</span>
          )}
          {hint && <span className="fieldset-label">{hint}</span>}
        </legend>
      )}
      <label className={clsx('input w-full', error && 'input-error', inputLabelClass)}>
        {leftContent}
        <input
          ref={ref}
          {...restProps}
          id={restProps.name ? `${restProps.name}-validation` : undefined}
          tabIndex={restProps.readOnly || restProps.disabled ? -1 : 0}
          onMouseDown={(e) => {
            if (restProps.readOnly || restProps.disabled) {
              e.preventDefault();
            }
          }}
          className="grow"
        />
        {rightContent}
      </label>
    </fieldset>
  );
}
